import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Provider } from "./ProviderCard";

const PROVIDERS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  storage: [{ value: "s3", label: "Amazon S3" }],
  email: [
    { value: "smtp", label: "SMTP" },
    { value: "resend", label: "Resend" },
  ],
  ai: [
    { value: "anthropic", label: "Anthropic" },
    { value: "openai", label: "OpenAI" },
    { value: "google", label: "Google" },
    { value: "ollama", label: "Ollama" },
  ],
};

interface CredentialField {
  key: string;
  label: string;
  type: "text" | "password" | "number" | "checkbox";
  required?: boolean;
}

interface SettingsField {
  key: string;
  label: string;
  placeholder?: string;
  required?: boolean;
}

function getCredentialFields(providerName: string): CredentialField[] {
  switch (providerName) {
    case "s3":
      return [
        { key: "accessKeyId", label: "Access Key ID", type: "text" },
        { key: "secretAccessKey", label: "Secret Access Key", type: "password" },
      ];
    case "smtp":
      return [
        { key: "host", label: "Host", type: "text", required: true },
        { key: "port", label: "Port", type: "number" },
        { key: "username", label: "Username", type: "text" },
        { key: "password", label: "Password", type: "password" },
        { key: "secure", label: "Use TLS", type: "checkbox" },
      ];
    case "resend":
      return [{ key: "apiKey", label: "API Key", type: "password", required: true }];
    case "anthropic":
    case "openai":
    case "google":
      return [{ key: "apiKey", label: "API Key", type: "password", required: true }];
    case "ollama":
      return [];
    default:
      return [];
  }
}

function getSettingsFields(providerName: string): SettingsField[] {
  switch (providerName) {
    case "s3":
      return [
        { key: "region", label: "Region", placeholder: "us-east-1", required: true },
        { key: "bucket", label: "Bucket", placeholder: "my-bucket", required: true },
        {
          key: "endpoint",
          label: "Custom Endpoint",
          placeholder: "https://account.r2.cloudflarestorage.com",
        },
        { key: "publicUrl", label: "Public URL", placeholder: "https://cdn.example.com" },
      ];
    case "smtp":
    case "resend":
      return [
        {
          key: "fromAddress",
          label: "From Address",
          placeholder: "noreply@example.com",
          required: true,
        },
        { key: "fromName", label: "From Name", placeholder: "My App" },
      ];
    case "anthropic":
    case "openai":
    case "google":
      return [{ key: "baseUrl", label: "Base URL", placeholder: "Default API endpoint" }];
    case "ollama":
      return [
        {
          key: "baseUrl",
          label: "Base URL",
          placeholder: "http://localhost:11434",
          required: true,
        },
      ];
    default:
      return [];
  }
}

const formSchema = z.object({
  type: z.enum(["storage", "email", "ai"]),
  providerName: z.string().min(1, "Provider is required"),
  displayName: z.string().min(1, "Display name is required"),
  credentials: z.record(z.string(), z.any()).default({}),
  settings: z.record(z.string(), z.string().optional()).default({}),
});

type FormValues = z.infer<typeof formSchema>;

interface ProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: Provider | null;
  defaultType?: "storage" | "email" | "ai";
  onSaved: () => void;
}

export function ProviderDialog({
  open,
  onOpenChange,
  provider,
  defaultType,
  onSaved,
}: ProviderDialogProps) {
  const isEdit = !!provider;
  const initialType = provider?.type ?? defaultType ?? "storage";

  const form = useForm<FormValues, any, FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      type: initialType,
      providerName: provider?.providerName ?? "",
      displayName: provider?.displayName ?? "",
      credentials: {},
      settings: provider?.settings ?? {},
    },
  });

  const watchType = form.watch("type");
  const watchProviderName = form.watch("providerName");

  const availableProviders = useMemo(() => PROVIDERS_BY_TYPE[watchType] ?? [], [watchType]);
  const credentialFields = useMemo(
    () => getCredentialFields(watchProviderName),
    [watchProviderName],
  );
  const settingsFields = useMemo(() => getSettingsFields(watchProviderName), [watchProviderName]);

  useEffect(() => {
    if (open) {
      form.reset({
        type: provider?.type ?? defaultType ?? "storage",
        providerName: provider?.providerName ?? "",
        displayName: provider?.displayName ?? "",
        credentials: {},
        settings: provider?.settings ?? {},
      });
    }
  }, [open, provider, defaultType, form]);

  // When type changes, reset providerName if current selection is not valid for new type
  useEffect(() => {
    const current = form.getValues("providerName");
    const valid = (PROVIDERS_BY_TYPE[watchType] ?? []).some((p) => p.value === current);
    if (!valid) {
      form.setValue("providerName", "");
    }
  }, [watchType, form]);

  async function onSubmit(values: FormValues) {
    // Filter out empty credential values (on edit, empty means "keep existing")
    const credentials: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(values.credentials)) {
      if (typeof val === "string" && val.trim() === "") continue;
      credentials[key] = val;
    }

    // Filter out empty settings values
    const settings: Record<string, string> = {};
    for (const [key, val] of Object.entries(values.settings)) {
      if (val && val.trim() !== "") {
        settings[key] = val.trim();
      }
    }

    const body = {
      type: values.type,
      providerName: values.providerName,
      displayName: values.displayName,
      credentials,
      settings,
    };

    try {
      const url = isEdit ? `/api/providers/${provider.id}` : "/api/providers";
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to ${isEdit ? "update" : "create"} provider`);
      }

      toast.success(`Provider ${isEdit ? "updated" : "created"}`);
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "An error occurred");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Provider" : "Add Provider"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the provider configuration."
              : "Configure a new provider for your workspace."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Type */}
            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="ai">AI</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Provider name */}
            <FormField
              control={form.control}
              name="providerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange} disabled={isEdit}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableProviders.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Display name */}
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display Name</FormLabel>
                  <FormControl>
                    <Input placeholder="My provider" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Dynamic credential fields */}
            {credentialFields.length > 0 && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-medium">Credentials</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {credentialFields.map((cf) =>
                    cf.type === "checkbox" ? (
                      <FormField
                        key={cf.key}
                        control={form.control}
                        name={`credentials.${cf.key}`}
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-2 sm:col-span-2">
                            <FormControl>
                              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                            <FormLabel className="!mt-0">{cf.label}</FormLabel>
                          </FormItem>
                        )}
                      />
                    ) : (
                      <FormField
                        key={cf.key}
                        control={form.control}
                        name={`credentials.${cf.key}`}
                        render={({ field }) => (
                          <FormItem className={cf.type === "password" ? "sm:col-span-2" : ""}>
                            <FormLabel>{cf.label}</FormLabel>
                            <FormControl>
                              <Input
                                type={
                                  cf.type === "number"
                                    ? "number"
                                    : cf.type === "password"
                                      ? "password"
                                      : "text"
                                }
                                placeholder={
                                  isEdit &&
                                  cf.type === "password" &&
                                  provider?.credentials?.[
                                    `has${cf.key.charAt(0).toUpperCase() + cf.key.slice(1)}`
                                  ]
                                    ? "Leave blank to keep existing"
                                    : undefined
                                }
                                value={(field.value as string) ?? ""}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ),
                  )}
                </div>
              </div>
            )}

            {/* Dynamic settings fields */}
            {settingsFields.length > 0 && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-medium">Settings</p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {settingsFields.map((sf) => (
                    <FormField
                      key={sf.key}
                      control={form.control}
                      name={`settings.${sf.key}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {sf.label}
                            {sf.required && " *"}
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={sf.placeholder}
                              value={(field.value as string) ?? ""}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? "Saving..."
                  : isEdit
                    ? "Save Changes"
                    : "Add Provider"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
